import React, { useState, useEffect } from 'react';
import { Box, Button, Paper, Typography, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import GetAppIcon from '@mui/icons-material/GetApp';
import { SHUTDOWN_MODE } from '../shutdownConfig';

const PWAPromptContainer = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  bottom: 16,
  left: 16,
  right: 16,
  zIndex: 1300,
  background: 'rgba(17, 24, 39, 0.95)',
  backdropFilter: 'blur(12px)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  borderRadius: theme.spacing(2),
  padding: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
  [theme.breakpoints.up('sm')]: {
    left: 'auto',
    right: 16,
    bottom: 16,
    maxWidth: 400,
  },
}));

const InstallButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #3BA1FF 0%, #0396FF 100%)',
  color: 'white',
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: '0 4px 16px rgba(59, 161, 255, 0.3)',
  '&:hover': {
    background: 'linear-gradient(135deg, #2196F3 0%, #0288D1 100%)',
    boxShadow: '0 6px 20px rgba(59, 161, 255, 0.4)',
  },
}));

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.navigator.standalone || window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check if already dismissed
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissed) {
      return;
    }

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show prompt after a delay
      setTimeout(() => {
        setShowPrompt(true);
      }, 3000);
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      setShowPrompt(false);
      setIsInstalled(true);
      localStorage.setItem('pwa-installed', 'true');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      setShowPrompt(false);
      setIsInstalled(true);
    }

    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
  };

  if (!showPrompt || isInstalled || SHUTDOWN_MODE) {
    return null;
  }

  return (
    <PWAPromptContainer elevation={8}>
      <GetAppIcon sx={{ color: '#3BA1FF', fontSize: 32 }} />
      <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle1" sx={{ color: 'rgba(255,255,255,0.95)', fontWeight: 600 }}>
          Install ScanTag App
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
          Get quick access and offline features
        </Typography>
      </Box>
      <InstallButton
        variant="contained"
        size="small"
        onClick={handleInstallClick}
        startIcon={<GetAppIcon />}
      >
        Install
      </InstallButton>
      <IconButton
        size="small"
        onClick={handleDismiss}
        sx={{ color: 'rgba(255,255,255,0.5)' }}
      >
        <CloseIcon />
      </IconButton>
    </PWAPromptContainer>
  );
};

export default PWAInstallPrompt; 